import { ExternalLinkIcon, LogOutIcon, MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';

import { useTheme, type ThemePreference } from '@/app/theme';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { env } from '@/config/env';
import { useAuth } from '@/features/auth/auth-provider';

import { LocaleIndicator } from './locale-indicator';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function Topbar({ children }: { children?: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();

  return (
    <header className="z-30 flex h-16 shrink-0 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur sm:px-6">
      {children}

      {/* Spacer: page crumbs live in PageHeader. Duplicating them here caused
          React key collisions (Content/Content) and a second breadcrumb strip. */}
      <div className="min-w-0 flex-1" />

      <div className="ml-auto flex items-center gap-1">
        <LocaleIndicator />

        <Button variant="ghost" size="sm" asChild>
          <a href={env.siteUrl} target="_blank" rel="noreferrer noopener">
            <ExternalLinkIcon aria-hidden="true" />
            <span className="hidden sm:inline">View site</span>
          </a>
        </Button>

        <Button
          variant="ghost"
          size="icon"
          aria-label={resolvedTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          title={resolvedTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          onClick={() => {
            // One-click flip between the two reading surfaces. "System" stays
            // available in the account menu for editors who want OS sync.
            setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
          }}
        >
          {resolvedTheme === 'light' ? (
            <MoonIcon aria-hidden="true" />
          ) : (
            <SunIcon aria-hidden="true" />
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Account menu">
              <Avatar className="size-7">
                <AvatarFallback className="text-xs">
                  {user ? initials(user.name) : '?'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate text-sm font-medium">{user?.name ?? 'Unknown'}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {user?.email ?? ''}
              </span>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Appearance
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => {
                setTheme(value as ThemePreference);
              }}
            >
              <DropdownMenuRadioItem value="light">
                <SunIcon aria-hidden="true" /> Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon aria-hidden="true" /> Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon aria-hidden="true" /> System
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                void logout();
              }}
            >
              <LogOutIcon aria-hidden="true" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
